import { useTranslation } from '@pasteltech/react-i18n'
import { FontWeight } from '@pasteltech/theme-provider'
import { graphql, Link, useStaticQuery } from 'gatsby'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { styled } from 'styled-components'
import { PagePaths } from '../../constants'
import { CloseIcon, LogoIcon, MenuIcon } from '../../svg-components'
import { ScreenQuery, ScreenSize, useScreenSize } from '../../utilities'
import { FacebookButton, WhatsappButton } from '../button'

export const APP_BAR_HEIGHT = 80
export const MOBILE_APP_BAR_HEIGHT = 80
export const MOBILE_APP_BAR_DRAWER_WIDTH = 280

const Container = styled.div`
  width: 100%;
  box-sizing: border-box;
  position: fixed;
  top: 0;

  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 100;
  background-color: ${({ theme }) => theme.colors.secondary.main};
  font-size: ${({ theme }) => theme.sizes.fontSize.button}px;

  ${ScreenQuery.desktop} {
    height: ${APP_BAR_HEIGHT}px;
    padding: ${({ theme }) =>
      `${theme.sizes.spacings.lg}px ${theme.sizes.spacings.xxxxl}px`};
  }
  ${ScreenQuery.tablet} {
    height: ${MOBILE_APP_BAR_HEIGHT}px;
    justify-content: center;
    padding: ${({ theme }) =>
      `${theme.sizes.spacings.lg}px ${theme.sizes.spacings.xl}px`};
  }
  ${ScreenQuery.mobile} {
    height: ${MOBILE_APP_BAR_HEIGHT}px;
    justify-content: center;
    padding: ${({ theme }) =>
      `${theme.sizes.spacings.lg}px ${theme.sizes.spacings.xl}px`};
  }
  white-space: nowrap;
`

// Desktop Navigation
const DesktopLeftContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => `${theme.sizes.spacings.lg}px`};
  align-items: center;
  flex: 1;
`

const DesktopRightContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => `${theme.sizes.spacings.xl}px`};
  flex: 1;
`

// Mobile Navigation
const MobileLogo = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  & svg {
    width: 80%;
  }
`
const StyledLink = styled(Link)`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.primary.mainContrast};
  font-weight: ${FontWeight.medium};
  ${ScreenQuery.mobile} {
    padding: ${({ theme }) => theme.sizes.spacings.md}px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  ${ScreenQuery.tablet} {
    padding: ${({ theme }) => theme.sizes.spacings.md}px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`
const StyledTextLink = styled(StyledLink)`
  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
  &:active {
    opacity: 0.6;
  }
`
const MenuIconContainer = styled.button<{ $isOpen: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;

  height: ${APP_BAR_HEIGHT}px;
  position: fixed;
  right: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary.mainContrast};
  transition: all 0.3s ease-in-out;
  path {
    fill: ${({ theme }) => theme.colors.primary.mainContrast};
  }
  padding: ${({ theme }) => theme.sizes.spacings.sm}px;
`

// Drawer Styles
const DrawerOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
`

const DrawerContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: min(${MOBILE_APP_BAR_DRAWER_WIDTH}px, 100vw);
  height: 100vh;
  background-color: ${({ theme }) => theme.colors.secondary.main};
  z-index: 1000;
  transform: ${({ $isOpen }) =>
    $isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease-in-out;

  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.sizes.spacings.lg}px;
`

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizes.spacings.xl}px;
  flex: 1;
  margin-top: ${MOBILE_APP_BAR_HEIGHT}px;
`

const DrawerButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.sizes.spacings.lg}px;
  margin-top: ${({ theme }) => theme.sizes.spacings.xxl}px;
`

const StyledCloseIcon = styled(CloseIcon)`
  path {
    fill: ${({ theme }) => theme.colors.primary.mainContrast};
  }
  cursor: pointer;
`
const CloseIconContainer = styled.button`
  height: ${MOBILE_APP_BAR_HEIGHT}px;
  z-index: 1000;
  position: absolute;
  left: 0;
  top: 0;
  background: transparent;
  border: none;
`

export const AppBar = memo(() => {
  const view = useScreenSize()
  const isMobileAppBar = useMemo(
    () => view === ScreenSize.mobile || view === ScreenSize.tablet,
    [view],
  )
  const data = useStaticQuery<Queries.AppBarQuery>(graphql`
    query AppBar {
      strapiAppBar {
        whatsappLink
        facebookLink
      }
    }
  `)

  const { t } = useTranslation()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  // Disable background scrolling when drawer is open
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    // Cleanup function to restore overflow when component unmounts
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isDrawerOpen])

  const links = useMemo(() => {
    const linkArray = [
      {
        label: t('appBar.aboutUs'),
        path: PagePaths.aboutUs,
      },
      {
        label: t('appBar.service'),
        path: PagePaths.services,
      },
      {
        label: t('appBar.showcase'),
        path: PagePaths.showcase,
      },
      {
        label: t('appBar.contactUs'),
        path: PagePaths.contactUs,
      },
    ]
    if (isMobileAppBar) {
      return [
        {
          label: t('appBar.homePage'),
          path: PagePaths.home,
        },
        ...linkArray,
      ]
    }
    return linkArray
  }, [isMobileAppBar, t])
  if (data.strapiAppBar?.facebookLink == null) {
    throw new Error('Facebook link is missing')
  }
  if (data.strapiAppBar?.whatsappLink == null) {
    throw new Error('WhatsApp link is missing')
  }

  const handleMenuClick = useCallback(() => {
    setIsDrawerOpen(!isDrawerOpen)
  }, [isDrawerOpen])

  const handleDrawerClose = useCallback(() => {
    setIsDrawerOpen(false)
  }, [])

  const handleLinkClick = useCallback(() => {
    setIsDrawerOpen(false)
  }, [])

  return (
    <>
      <Container>
        {/* Desktop Navigation */}
        {!isMobileAppBar ? (
          <>
            <DesktopLeftContainer>
              <StyledLink to={PagePaths.home}>
                <LogoIcon />
              </StyledLink>
              {links.map((link) => (
                <StyledTextLink key={link.path} to={link.path}>
                  {link.label}
                </StyledTextLink>
              ))}
            </DesktopLeftContainer>
            <DesktopRightContainer>
              <FacebookButton to={data.strapiAppBar.facebookLink} />
              <WhatsappButton to={data.strapiAppBar.whatsappLink} />
            </DesktopRightContainer>
          </>
        ) : (
          <>
            <MobileLogo to={PagePaths.home}>
              <LogoIcon />
            </MobileLogo>
            <MenuIconContainer $isOpen={isDrawerOpen} onClick={handleMenuClick}>
              <MenuIcon />
            </MenuIconContainer>
          </>
        )}
      </Container>

      {/* Mobile Drawer */}
      {isMobileAppBar && (
        <>
          <DrawerOverlay $isOpen={isDrawerOpen} onClick={handleDrawerClose} />
          <DrawerContainer $isOpen={isDrawerOpen}>
            <CloseIconContainer>
              <StyledCloseIcon onClick={handleDrawerClose} />
            </CloseIconContainer>
            <DrawerContent>
              {links.map((link) => (
                <StyledTextLink
                  key={link.path}
                  to={link.path}
                  onClick={handleLinkClick}
                >
                  {link.label}
                </StyledTextLink>
              ))}
              <DrawerButtonContainer>
                <FacebookButton to={data.strapiAppBar.facebookLink} />
                <WhatsappButton to={data.strapiAppBar.whatsappLink} />
              </DrawerButtonContainer>
            </DrawerContent>
          </DrawerContainer>
        </>
      )}
    </>
  )
})
AppBar.displayName = 'AppBar'
